import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useAccount } from '../context/AccountContext';
import DesktopLayout from '../layouts/DesktopLayout';
import AccountSwitcher from '../components/common/AccountSwitcher';
import AvailabilityGrid from '../components/availabilityGrid/AvailabilityGrid';
import { useAvailabilityGrid } from '../hooks/useAvailabilityGrid';
import {
  AVAILABILITY_STATE,
  AVAILABILITY_VIEW,
  CLOSED_NIGHT_LINE,
  resolveAvailabilityState
} from '../utils/availabilityUiHelpers';
import { formatDate } from '../utils/dateFormatter';
import { formatCount } from '../utils/numberFormatter';
import { getPlatformColor } from '../utils/platformUtils';
import { GRID_TEXT, STATUS_OPTIONS, BEDROOM_OPTIONS } from '../config/availabilityGridLabels';
import { PRICING_ROLES } from '../config/roleConfig';
import '../components/availabilityGrid/availability-grid.css';

/**
 * «التقويم» — every unit × every night on one page, so ops stop opening unit calendars
 * one at a time (tasks/multi-unit-availability-grid.md).
 *
 * READ-ONLY by decision: a night opens the unit calendar on that date, a booking opens the
 * booking. There is no second place to change availability.
 *
 * The owner filter is the app-wide account switcher, not a page control — the same account
 * the other ops pages scope by, passed to the query as `ownerId` and part of the cache key.
 * Arabic-only ops page: its words live in config/availabilityGridLabels.js.
 */

const NARROW_QUERY = '(max-width: 767.98px)';
const { AVAILABLE, BLOCKED, STOP_SELL } = AVAILABILITY_STATE;

// The ONE shared resolver the unit calendar uses (plan §9) — the grid never decides a state.
const stateOf = (night) => resolveAvailabilityState({
  available: night.available,
  booked: night.booked,
  blocked: night.blocked,
  total: night.total,
  stopSell: night.stopSell,
  bookingCount: night.bookingCount,
  hasData: night.hasRow
});

/** Phones get a 7-night window; the column count is the only thing that changes. */
const useNarrow = () => {
  const [narrow, setNarrow] = useState(() => window.matchMedia?.(NARROW_QUERY).matches ?? false);
  useEffect(() => {
    const mq = window.matchMedia?.(NARROW_QUERY);
    if (!mq) return undefined;
    const onChange = (e) => setNarrow(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return narrow;
};

const AvailabilityGridPage = () => {
  const { user } = useContext(AuthContext);
  const { selectedAccount } = useAccount();
  const [activeNavItem, setActiveNavItem] = useState('units-calendar');
  // Ops-manager sees the Airbnb offers here but cannot open the pricing page (tasks/ops-calendar-airbnb-offers.md D1).
  const canOpenPricing = PRICING_ROLES.includes(user?.role);
  const narrow = useNarrow();

  // `start` undefined = the server default (yesterday, so today's checkouts show), resolved by the server in the business timezone. Paging uses
  // the server's prevStart / nextStart — the browser never does date arithmetic.
  const [start, setStart] = useState(undefined);
  const [windowDays, setWindowDays] = useState(30);
  const [status, setStatus] = useState('active');
  const [bedrooms, setBedrooms] = useState('');
  const [search, setSearch] = useState('');
  const [freeOnly, setFreeOnly] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // A building belongs to an owner. The pick remembers the account it was made under and is
  // simply not applied under another — no reset effect, no wasted request with a stale building.
  const [buildingPick, setBuildingPick] = useState({ account: selectedAccount, value: '' });
  const building = buildingPick.account === selectedAccount ? buildingPick.value : '';
  const setBuilding = (value) => setBuildingPick({ account: selectedAccount, value });

  const params = useMemo(() => {
    const p = { days: narrow ? 7 : windowDays, status };
    if (start) p.start = start;
    if (selectedAccount) p.ownerId = selectedAccount;
    if (building) p.building = building;
    if (bedrooms) p.bedrooms = bedrooms;
    return p;
  }, [narrow, windowDays, status, start, selectedAccount, building, bedrooms]);

  const grid = useAvailabilityGrid(params);
  const data = grid.data;
  const win = data?.window;
  // The previous result stays on screen while a new filter set loads — dimmed and inert, so it
  // can never be read as the answer to the new filters.
  const stale = grid.isPlaceholderData;

  // Read defensively: a frontend deployed ahead of the backend must not crash on a missing field.
  const annotated = useMemo(() => {
    const columns = win?.dates?.length || 0;
    return (data?.units || []).map(u => {
      const nights = (u.nights || []).slice(0, columns);
      return { ...u, owners: u.owners || [], bookings: u.bookings || [], nights, states: nights.map(stateOf) };
    });
  }, [data, win]);

  // Search and "has a free night" refine the rows the server already returned.
  const units = useMemo(() => {
    const q = search.trim().toLowerCase();
    return annotated.filter(u =>
      (!q || `${u.code} ${u.unitNumber} ${u.name} ${u.building}`.toLowerCase().includes(q))
      && (!freeOnly || u.states.includes(AVAILABLE)));
  }, [annotated, search, freeOnly]);

  const freeCounts = useMemo(
    () => (win?.dates || []).map((_, i) => units.reduce((n, u) => n + (u.states[i] === AVAILABLE ? 1 : 0), 0)),
    [win, units]
  );

  const activeFilters = [building, status !== 'active', bedrooms, search.trim(), freeOnly].filter(Boolean).length;
  const resetFilters = () => {
    setBuilding('');
    setStatus('active');
    setBedrooms('');
    setSearch('');
    setFreeOnly(false);
  };

  const first = win?.dates?.[0];
  const last = win?.dates?.[win.dates.length - 1];
  // The year is dropped from the first date only when both ends share it.
  const rangeLabel = first
    ? `${formatDate(first, first.slice(0, 4) === last.slice(0, 4) ? { year: undefined } : {})} – ${formatDate(last)}`
    : '';

  return (
    <DesktopLayout
      activeNavItem={activeNavItem}
      setActiveNavItem={setActiveNavItem}
      headerTitle={GRID_TEXT.pageTitle}
    >
      <div className="ag" dir="rtl">
        <div style={{ marginBottom: '1rem' }}>
          <AccountSwitcher />
        </div>
        <p className="ag-intro">{GRID_TEXT.intro}</p>

        <div className="ag-toolbar">
          <div className="ag-pager">
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setStart(undefined)}>
              {GRID_TEXT.today}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              aria-label={GRID_TEXT.prev}
              disabled={!win}
              onClick={() => setStart(win.prevStart)}
            >
              <i className="bi bi-chevron-right" />
            </button>
            <span className="ag-range">{rangeLabel}</span>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              aria-label={GRID_TEXT.next}
              disabled={!win}
              onClick={() => setStart(win.nextStart)}
            >
              <i className="bi bi-chevron-left" />
            </button>
            {grid.isFetching && !grid.isLoading && (
              <span className="spinner-border spinner-border-sm text-primary" role="status" aria-label={GRID_TEXT.refreshing} />
            )}
          </div>
          <div className="ag-pager">
            <div className="btn-group btn-group-sm ag-window-toggle" role="group" aria-label={GRID_TEXT.windowGroup}>
              {[14, 30].map(d => (
                <button
                  key={d}
                  type="button"
                  className={`btn ${windowDays === d ? 'btn-primary' : 'btn-outline-primary'}`}
                  aria-pressed={windowDays === d}
                  onClick={() => setWindowDays(d)}
                >
                  {GRID_TEXT.windowDays(formatCount(d))}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm ag-filters-toggle"
              aria-expanded={filtersOpen}
              onClick={() => setFiltersOpen(o => !o)}
            >
              <i className="bi bi-funnel" style={{ marginInlineEnd: 4 }} />
              {activeFilters ? GRID_TEXT.filtersActive(formatCount(activeFilters)) : GRID_TEXT.filters}
            </button>
          </div>
        </div>

        <div className={`ag-filters${filtersOpen ? '' : ' ag-filters--collapsed'}`}>
          <label className="ag-field">
            {GRID_TEXT.building}
            <select id="ag-building" className="form-select form-select-sm" value={building} onChange={e => setBuilding(e.target.value)}>
              <option value="">{GRID_TEXT.allBuildings}</option>
              {(data?.options?.buildings || []).map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </label>
          <label className="ag-field">
            {GRID_TEXT.status}
            <select id="ag-status" className="form-select form-select-sm" value={status} onChange={e => setStatus(e.target.value)}>
              {STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label className="ag-field">
            {GRID_TEXT.bedrooms}
            <select id="ag-bedrooms" className="form-select form-select-sm" value={bedrooms} onChange={e => setBedrooms(e.target.value)}>
              {BEDROOM_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label className="ag-field ag-field--search">
            {GRID_TEXT.search}
            <input
              id="ag-search"
              type="search"
              className="form-control form-control-sm"
              placeholder={GRID_TEXT.searchPlaceholder}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </label>
          <label className="ag-check">
            <input id="ag-free" type="checkbox" className="form-check-input" checked={freeOnly} onChange={e => setFreeOnly(e.target.checked)} />
            {GRID_TEXT.freeOnly}
          </label>
          <button type="button" className="btn btn-link btn-sm" onClick={resetFilters}>{GRID_TEXT.resetFilters}</button>
        </div>

        <div className="ag-legend" aria-label={GRID_TEXT.legendLabel}>
          <span title={GRID_TEXT.legendBookedHint}>
            <i className="ag-swatch" style={{ background: getPlatformColor('airbnb'), borderColor: 'transparent' }} />
            {GRID_TEXT.legendBooked}
          </span>
          <span>
            <i className="ag-swatch" style={{ backgroundColor: AVAILABILITY_VIEW[BLOCKED].cellBg, backgroundImage: CLOSED_NIGHT_LINE }} />
            {GRID_TEXT.legendHeld}
          </span>
          <span>
            <i className="ag-swatch" style={{ backgroundColor: AVAILABILITY_VIEW[STOP_SELL].cellBg, backgroundImage: CLOSED_NIGHT_LINE }} />
            {GRID_TEXT.legendStopSell}
          </span>
          <span title={GRID_TEXT.legendUnknownHint}><i className="ag-swatch">؟</i>{GRID_TEXT.legendUnknown}</span>
          <span title={GRID_TEXT.legendMismatchHint}><i className="ag-swatch ag-swatch--mismatch" />{GRID_TEXT.legendMismatch}</span>
        </div>

        {grid.isLoading && (
          <div className="text-center" style={{ padding: '3rem 0' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{GRID_TEXT.loading}</span>
            </div>
          </div>
        )}

        {/* A FAILED fetch is not an empty estate — say so, and offer the retry. */}
        {grid.isError && !data && (
          <div className="alert alert-light border" role="alert">
            {GRID_TEXT.loadError}
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              style={{ marginInlineStart: 8 }}
              onClick={() => grid.refetch()}
            >
              {GRID_TEXT.retry}
            </button>
          </div>
        )}

        {data && data.unitsInScope === 0 && <div className="ag-empty">{GRID_TEXT.noUnits}</div>}

        {data && data.unitsInScope > 0 && units.length === 0 && (
          <div className="ag-empty">
            {GRID_TEXT.noMatch}
            <button type="button" className="btn btn-link btn-sm" onClick={resetFilters}>{GRID_TEXT.resetFilters}</button>
          </div>
        )}

        {data && units.length > 0 && (
          <div className={stale ? 'ag-stale' : undefined} aria-busy={stale}>
            <AvailabilityGrid win={win} units={units} freeCounts={freeCounts} canOpenPricing={canOpenPricing} />
          </div>
        )}

        {data && (
          <div className="ag-stats">
            <span>{GRID_TEXT.shown(formatCount(units.length), formatCount(data.unitsInScope))}</span>
            {grid.isError && <span className="text-danger">{GRID_TEXT.refreshError}</span>}
          </div>
        )}
      </div>
    </DesktopLayout>
  );
};

export default AvailabilityGridPage;
